import React, { useEffect, PureComponent, useContext, useState, memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { useDropzone } from 'react-dropzone';

import Icon from './Icon';
import FieldTypes from '../../constants/FieldTypes';
import ThemeIcons from '../../theme/Icons';
import ThemeContext from '../../theme/ThemeContext';

import faExclamationTriangle from '../../theme/fortawesomeIcons/free-solid-svg-icons/faExclamationTriangle';
import { Red } from '../../constants/Colors';
import LoadingIndicator from './LoadingIndicator';

const StyledLoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

export const FadeAnimation = keyframes`
  0% {
    opacity: 1;
    visibility: visible;
  }
  100% {
    opacity: 0;
    visibility: hidden;
  }
`;

const StyledFailureWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  animation: ${FadeAnimation} 1s ease-in 0s forwards;
`;

const ImageManager = memo(({
  type,
  disabled,
  multiple,
  isMobileView,
  value,
  enableSystemFilePicker,
  inputGroupEmbedded,

  onUpload,
  onClick,
  onFocus,
  onDelete,
  onLoadCompleted,

  maxWidth,
  maxHeight,

  fontColor,
  borderRadius,
  borderWidth,
  borderColor,
  padding,
  backgroundColor,
  primaryColor,
  dangerColor,
  secondaryColor,
  minHeight

}) => {

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const theme = useContext(ThemeContext);

  /**
   * Important Note: Immediately fire onLoadCompleted for placeholder because
   * there are no images loading to wait for. This is very important for auto 
   * measuring and PDF rendering.
   */
  useEffect(() => {
    if (!value || value.length < 1) {
      onLoadCompleted();
    }
  }, [value]);

  /**
   * Files could be undefined if the user has disabled the file system upload
   * @param {*} files 
   */
  const handleUpload = useCallback((files) => {
    onUpload(
      (loading) => {
        setLoading(loading);
      }, (error) => {
        setError(error);
        setLoading(false);
        setTimeout(() => setError(false), 1000);
      },
      files
    );

  }, [onUpload]);

  if (loading) {
    return (
      <StyledLoadingWrapper>
        <LoadingIndicator size="lg" color={theme.primaryColor} />
      </StyledLoadingWrapper>
    );
  }

  if (error) {
    return (
      <StyledFailureWrapper>
        <Icon
          icon={faExclamationTriangle}
          color={Red}
          size="lg"
        />
      </StyledFailureWrapper>
    );
  }

  return !value || value.length < 1 ? (
    <ImageManagerDropzone
      {...{
        type,
        value,
        disabled,
        multiple,
        enableSystemFilePicker,
        inputGroupEmbedded,

        onUpload: handleUpload,
        onFocus,
        onLoadCompleted,

        fontColor,
        borderColor,
        borderWidth,
        borderRadius,
        backgroundColor,
        primaryColor,
        secondaryColor,
        minHeight,
        padding

      }}
    />
  ) : (
    <ImageManagerViewer
      {...{
        type,
        isMobileView,
        enableSystemFilePicker,
        disabled,
        multiple,
        value: Array.isArray(value) ? value : [value],

        maxWidth,
        maxHeight,
        fontColor,
        borderColor,
        borderWidth,
        borderRadius,
        backgroundColor,
        primaryColor,
        secondaryColor,
        dangerColor,
        padding,

        onDelete,
        onClick,
        onUpload: handleUpload,
        onFocus,
        onLoadCompleted,

      }}
    />
  );

});

const StyledImageViewerWrapper = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  border: 1px solid transparent;

  @media only screen 
    and (min-device-width: 375px) 
    and (max-device-width: 812px) 
    and (-webkit-min-device-pixel-ratio: 3)
    and (orientation: portrait) { 
      overflow-y: auto !important;
  }

  #image-manager-action-toolbar {
    display: ${(props) => (!props.autoShow ? 'none' : 'block')};
  }

  &:hover {

    #image-manager-action-toolbar {
      display: block;
    }

    border: 1px solid ${(props) => (props.disabled ? 'transparent' : props.theme.borderColor)};
    border-radius: ${(props) => props.theme.borderRadius}px;

  }

`;

const StyledImageViewerScroller = styled.div`
  height: 100%;
  overflow: hidden;
  ${(props) => {

    if (!props.multiple) {
      return '';
    } else {
      return `  
        scrollbar-gutter: stable;
        overflow-y: auto;
      `;
    }

  }}

`;

const StyledImageViewerInternalWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  align-content: flex-start;
`;

const StyledImageViewerPaddingWrapper = styled.div` 
  box-sizing: border-box;
  padding: ${(props) => (props.padding ? `${props.padding}px` : '0px')};
  width: ${(props) => props.width};
  height: ${(props) => props.height};
  hover: {
    cursor: ${(props) => props.enableHover ? 'pointer' : 'default'};
  }
`;

const StyledImageViewer = styled.div`
  position: relative;
  box-sizing: border-box;
  border: ${(props) => props.border};
  border-radius: ${(props) => props.borderRadius}px;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;

  #image-viewer-delete-icon {
    visibility: ${(props) => (!props.autoShow ? 'hidden' : 'visible')};
  }

  &:hover {

    #image-viewer-delete-icon {
      visibility: visible;
    }

  }
`;

const StyledImageViewerImage = styled.img`
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const StyledImageViewerIcon = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  border-radius: 100px;
  border: 1px solid transparent;
  background-color: ${(props) => props.theme.backgroundColor};
`;

const StyledImageViewerActionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
`;

export class ImageManagerViewer extends PureComponent {

  static propTypes = {

    type: PropTypes.string,
    value: PropTypes.array,
    disabled: PropTypes.bool,
    multiple: PropTypes.bool,
    enableSystemFilePicker: PropTypes.bool,

    onLoadCompleted: PropTypes.func,
    onDelete: PropTypes.func,
    onUpload: PropTypes.func,
    onClick: PropTypes.func,
    onFocus: PropTypes.func,

    borderWidth: PropTypes.number,
    borderColor: PropTypes.string,
    borderRadius: PropTypes.number,
    padding: PropTypes.number,
    maxWidth: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]), //We make this a string so we can use '200px' or '33%'
    maxHeight: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]), //We make this a string so we can use '200px' or '33%'
  }

  constructor(props) {
    super(props);
    this.state = { imageLoadUpdated: false };
    this.loadedImages = {};
  }

  componentDidUpdate(prevProps, prevState) {

    const { value, onLoadCompleted } = this.props;
    const { imageLoadUpdated } = this.state;

      if (prevState.imageLoadUpdated !== imageLoadUpdated && onLoadCompleted) {
        requestAnimationFrame(() => {
          if (Object.keys(this.loadedImages).length === value.length) {
            onLoadCompleted();
          }
        });
      }
  }

  render() {

    const {

      disabled,
      enableSystemFilePicker,
      multiple,
      value,
      isMobileView,

      onDelete,
      onClick,
      onUpload,
      onFocus,

      //Styles
      maxWidth,
      maxHeight,
      borderRadius,
      borderWidth,
      borderColor,
      padding,
      fontColor,
      primaryColor,
      dangerColor,
      backgroundColor,

      type,
    } = this.props;

    const theme = {
      borderColor,
      borderRadius,
      borderWidth,
      fontColor,
      backgroundColor,
      primaryColor,
      dangerColor
    };

    /**
     * We join width and color to a single border style because chrome displays
     * the border as a One tone color if you use border-color and border-width
     * indvidually.
     */
    const border = `${borderWidth}px solid ${borderColor}`;

    const imgStyles = {
      width: multiple && maxWidth ? maxWidth : '100%',
      height: '100%',
      borderRadius,
      padding,
      border,
    };

    /**
     * If multiple and maxHeight is set we set the height to maxHeight
     * If multiple is true and maxHeight is not set we set the height to auto
     */
    if (multiple && maxHeight) imgStyles.height = maxHeight;
    else if (multiple) imgStyles.height = 'auto';

    /**
     * We set the height to 100% if there is only one image in the field so that it can
     */
    if (value && value.length === 1 && !maxHeight) {
      imgStyles.height = '100%';
    }

    const imageNodes = value.map((imgData = {}, i) => {

      const { url } = imgData;

      return (
        <StyledImageViewerPaddingWrapper 
          key={i} 
          {...imgStyles} 
          enableHover={!!onClick}
          onClick={() => { if (onClick) onClick(i, imgData) }}
        >
          <StyledImageViewer
            {...imgStyles}
            key={i}
            autoShow={isMobileView}
          >
            <StyledImageViewerImage
              data-testid={`${i}-image-field`}
              src={url}
              onLoad={() => this.handleImageLoad(i)}
              onError={() => this.handleImageLoad(i)}
              {...imgStyles}
            />
            {disabled ? null : (
              <StyledImageViewerIcon theme={theme} id="image-viewer-delete-icon">
                <Icon
                  size="lg"
                  icon={ThemeIcons.settings.deleteSolid}
                  color={dangerColor}
                  hoverColor={dangerColor}
                  onClick={(e) => {
                    if (e) e.stopPropagation();
                    onFocus();
                    onDelete(i, imgData);
                  }}
                />
              </StyledImageViewerIcon>
            )}
          </StyledImageViewer>
        </StyledImageViewerPaddingWrapper>
      );

    });

    return (
      <StyledImageViewerWrapper theme={theme} disabled={disabled} autoShow={isMobileView}>
        <StyledImageViewerScroller {...{ multiple }}>
          <StyledImageViewerInternalWrapper
            {...{ value }}
          >
            {imageNodes}
          </StyledImageViewerInternalWrapper>
        </StyledImageViewerScroller>
        <StyledImageViewerActionWrapper>
          <ActionToolbar 
            {...{ 
              type, 
              multiple, 
              onUpload, 
              onFocus, 
              disabled, 
              isMobileView, 
              enableSystemFilePicker,
              ...theme 
            }} 
          />   
        </StyledImageViewerActionWrapper>
      </StyledImageViewerWrapper>
    );

  }

  handleImageLoad = (i) => {

    this.loadedImages[i] = true;

    /**
     * We do this timestamp update to trigger the componentDidUpdate check.
     */
    this.setState({ imageLoadUpdated: new Date().getTime() });

  }

}

const StyledActionToolbarWrapper = styled.div`
  position: absolute;
  bottom: 6px;
  right: 6px;
  display: flex;
  cursor:pointer;
  z-index: 1000;
`;

const StyledAction = styled.button`
  display: flex;
  flex-direction: row;
  background: ${(props) => props.theme.backgroundColor};;
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: ${(props) => props.theme.borderRadius}px;
  color: ${(props) => props.theme.fontColor};
  font-size: 12px;
  padding: 4px 8px;
  transition: all 0.15s ease-in-out;

  svg {
    color: ${(props) => props.theme.fontColor};
  }

  &:hover {
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')}; 
    border: 1px solid ${(props) => props.theme.primaryColor};
    color: ${(props) => props.theme.primaryColor};
    svg {
      color: ${(props) => props.theme.primaryColor};
    }
  }
`;

const StyledActionText = styled.div`
  margin-left: 4px;
  z-index: 1000;
`;

export const ActionToolbar = ({
  disabled,
  enableSystemFilePicker,
  multiple,

  onUpload,
  onFocus,

  primaryColor,
  fontColor,
  borderColor,
  borderRadius,
  type,
}) => {

  /**
   * <--------------NOTICE-------------->
   * This toolbar component is also used for file field. If you make
   * any changes here ensure that the toolbar functions properly for
   * both image and file fields.
   */

  //Logic to accept any file type for file field
  let acceptedFileTypes = {};

  if (type === FieldTypes.image) {
    //Logic below allows only images for the image field
    acceptedFileTypes = {
      'image/jpeg': ['.jpg', '.jpeg', '.JPG', '.JPEG'],
      'image/png': ['.png', '.PNG'],
    };

  }

  const theme = useContext(ThemeContext);

  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptedFileTypes,
    onDrop: onUpload,
    multiple
  });

  if (disabled) return null;

  const uploadHandlerNode = !enableSystemFilePicker ? (
    <StyledAction
      theme={{
        backgroundColor: theme.backgroundColor,
        primaryColor,
        fontColor,
        borderColor,
        borderRadius,
      }}
      onClick={() => onUpload()}
    >
      <Icon
        icon={ThemeIcons.settings.upload}
        size="1x"
      />
      <StyledActionText>Upload</StyledActionText>
    </StyledAction>
  ) : (
    <StyledAction
      theme={{
        backgroundColor: theme.backgroundColor,
        primaryColor,
        fontColor,
        borderColor,
        borderRadius,
      }}
      {...getRootProps({
        onClick: () => onFocus()
      })}
    >
      {type === FieldTypes.image ? (
        <input
          {...getInputProps()}
          type="file"
          accept="image/*,android/force-camera-workaround"
        />
      ) : (
        <input {...getInputProps()} />
      )}
      <Icon
        icon={ThemeIcons.settings.upload}
        size="1x"
      />
      <StyledActionText>Upload</StyledActionText>
    </StyledAction>
  );

  return (
    <StyledActionToolbarWrapper id="image-manager-action-toolbar">
      {uploadHandlerNode}
    </StyledActionToolbarWrapper>
  );

};

const StyledImageDropzoneWrapper = styled.div`
  display: flex;
  align-items:center;
  justify-content: center;
  width: 100%;
  min-height: ${(props) => (props.inputGroupEmbedded ? '100%' : '100%')};
  height: ${(props) => (props.inputGroupEmbedded ? '100%' : '100%')};
  background: ${(props) => props.theme.backgroundColor};
  border: ${(props) => props.theme.borderWidth}px dashed ${(props) => props.theme.borderColor} !important;
  border-radius: ${(props) => props.theme.borderRadius}px;
  padding: ${(props) => (props.inputGroupEmbedded ? '2px 4px' : (props.padding !== undefined ? `${props.padding}px` : '0px'))};
  box-sizing: border-box;
  overflow: hidden;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  color: ${(props) => props.theme.fontColor};
  transition: all .15s ease-in-out;

  svg {
    color: ${(props) => props.theme.fontColor};
    font-size: 16px;
  }

  &:hover {
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
    color: ${(props) => (props.disabled ? props.theme.fontColor : props.theme.primaryColor)};
    border: ${(props) => props.theme.borderWidth}px dashed ${(props) => (props.disabled ? props.theme.borderColor : props.theme.primaryColor)} !important;
    border-radius: ${(props) => props.theme.borderRadius}px;

    svg {
      color: ${(props) => (props.disabled ? props.theme.fontColor : props.theme.primaryColor)};
      font-size: 16px;
    }
  }

  min-height: ${(props) => (props.minHeight ? `${props.minHeight}px` : '100%')};

`;

export const ImageManagerDropzone = memo(({
  type,
  fontColor,
  borderColor,
  borderRadius,
  borderWidth,
  primaryColor,
  secondaryColor,
  backgroundColor,
  minHeight,
  value,
  padding,

  multiple,
  disabled,
  enableSystemFilePicker,
  inputGroupEmbedded,

  onUpload,
  onFocus,
  onLoadCompleted

}) => {

  const theme = {
    fontColor,
    backgroundColor: backgroundColor || secondaryColor,
    borderColor,
    borderRadius,
    borderWidth,
    primaryColor,
  };


  /**
   * <--------------NOTICE-------------->
   * This toolbar component is also used for file field. If you make
   * any changes here ensure that the toolbar functions properly for
   * both image and file fields.
   */

  //Logic below allows any file type for the file field
  let acceptedFileTypes = {};

  if (type === FieldTypes.image) {

    //Logic below allows only images for the image field
    acceptedFileTypes = {
      'image/jpeg': ['.jpg', '.jpeg', '.JPG', '.JPEG'],
      'image/png': ['.png', '.PNG'],
    };

  }

  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptedFileTypes,
    onDrop: (files) => {
      if (multiple) onUpload(files);
      else onUpload([files[0]]); //Only use first image if multiple is false
    },
    noClick: disabled,
    multiple
  });

  const uploadHandlerNode = !enableSystemFilePicker ? (
    <StyledImageDropzoneWrapper
      theme={theme}
      minHeight={minHeight}
      padding={padding}
      inputGroupEmbedded={inputGroupEmbedded}
      onClick={() => {
        onFocus();
        onUpload();
      }}
    >
      <Icon icon={ThemeIcons.fields.upload} />
    </StyledImageDropzoneWrapper>
  ) : (
    <StyledImageDropzoneWrapper
      minHeight={minHeight}
      theme={theme}
      padding={padding}
      inputGroupEmbedded={inputGroupEmbedded}
      {...getRootProps({
        onClick: () => {
          onFocus();
        },
        onDrop: () => {
          onFocus();
        },
      })}
    >
      {type === FieldTypes.image ? (
        <input
          {...getInputProps()}
          type="file"
          accept="image/*,android/force-camera-workaround"
        />
      ) : (
        <input {...getInputProps()} />
      )}
      <Icon icon={ThemeIcons.settings.upload} />
    </StyledImageDropzoneWrapper>
  );

  return disabled ? (
    <StyledImageDropzoneWrapper theme={theme} padding={padding} inputGroupEmbedded={inputGroupEmbedded} disabled>
      <Icon icon={ThemeIcons.fields.upload} />
    </StyledImageDropzoneWrapper>
  ) : uploadHandlerNode;

});

export default ImageManager;
